import { createFileRoute } from "@tanstack/react-router";
import { SeoPageShell, H2 } from "@/components/SeoPageShell";

const TITLE = "Shop the Look — AI App to Find Clothes from Any Photo";
const DESC = "Like2Fit is the shop-the-look app that turns any outfit photo into shoppable products. Upload an Instagram or TikTok screenshot and buy every piece.";
const URL = "https://like2fit.com/shop-the-look";

export const Route = createFileRoute("/shop-the-look")({
  component: ShopTheLookPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://like2fit.com/" },
            { "@type": "ListItem", position: 2, name: "Shop the look", item: URL },
          ],
        }),
      },
    ],
  }),
});

function ShopTheLookPage() {
  return (
    <SeoPageShell
      eyebrow="Shop the look"
      h1="The shop-the-look app for any photo"
      intro="See an outfit you love on Instagram, TikTok or Pinterest? Upload the screenshot and Like2Fit finds every piece — exact matches when they exist, close alternatives at your budget when they don't."
    >
      <H2>Find clothes from a picture — no link required</H2>
      <p>Most "shop the look" features only work if the creator tagged the brand. Like2Fit doesn't need a tag. The AI reads the photo directly, so any image works — even old screenshots and street-style shots.</p>

      <H2>Match to hundreds of retailers</H2>
      <p>We search across hundreds of fashion retailers in real time, from fast-fashion staples to indie labels and premium designers. You decide the price range; we filter the results.</p>

      <H2>Three budget tiers, one outfit</H2>
      <p>Every result comes in budget, mid and premium tiers — so you can copy a designer outfit for under £100 or invest in the exact pieces. Set your budget once and Like2Fit remembers it.</p>

      <H2>Save the look, build a wardrobe</H2>
      <p>Save outfits you love to your Like2Fit wardrobe. Pair new finds with clothes you already own using Style My Wardrobe, and get daily outfit ideas based on what's in your closet.</p>
    </SeoPageShell>
  );
}
